import type {
  AddDictionaryItemReq,
  AddDictionaryReq,
  BaseErrorResponse,
  EmptySuccessResponse,
  GetDictionariesRes,
  GetDictionaryItemRes,
  GetDictionaryItemsRes,
  GetDictionaryRes,
} from './data-contracts';
import type { RequestParams } from './http-client';
import { ContentType, HttpClient } from './http-client';


export default function useDictionary() {
  class Dictionary<SecurityDataType = unknown> extends HttpClient<SecurityDataType> {
    /**
     * No description
     *
     * @tags Dictionary
     * @name DictionaryControllerGetDictionaryList
     * @summary Returns dictionary list
     * @request GET:/api3/dictionary
     * @secure
     * @response `200` `GetDictionariesRes`
     * @response `400` `BaseErrorResponse`
     */
    dictionaryControllerGetDictionaryList = (
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        type?: 'profile' | 'plan';
      },
      params: RequestParams = {},
    ) =>
      this.request<GetDictionariesRes, BaseErrorResponse>({
        path: '/api3/dictionary',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Dictionary
     * @name DictionaryControllerCreateDictionary
     * @summary Creates dictionary
     * @request POST:/api3/dictionary
     * @secure
     * @response `200` `GetDictionaryRes`
     * @response `400` `BaseErrorResponse`
     */
    dictionaryControllerCreateDictionary = (data: AddDictionaryReq, params: RequestParams = {}) =>
      this.request<GetDictionaryRes, BaseErrorResponse>({
        path: '/api3/dictionary',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Dictionary
     * @name DictionaryControllerUpdateDictionary
     * @summary Updates dictionary
     * @request PUT:/api3/dictionary/{id}
     * @secure
     * @response `200` `GetDictionaryRes`
     * @response `400` `BaseErrorResponse`
     */
    dictionaryControllerUpdateDictionary = (id: number, data: { title: string }, params: RequestParams = {}) =>
      this.request<GetDictionaryRes, BaseErrorResponse>({
        path: `/api3/dictionary/${id}`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Dictionary
     * @name DictionaryControllerDeleteDictionary
     * @summary Deletes dictionary
     * @request DELETE:/api3/dictionary/{id}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    dictionaryControllerDeleteDictionary = (
      id: number,
      query?: {
        /**
         * Hard remove item
         * @example true
         */
        hardRemove?: boolean;
      },
      params: RequestParams = {},
    ) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/dictionary/${id}`,
        method: 'DELETE',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Dictionary item
     * @name DictionaryItemControllerGetDictionaryItemList
     * @summary Returns dictionary item list
     * @request GET:/api3/dictionary/{id}/item
     * @secure
     * @response `200` `GetDictionaryItemsRes`
     * @response `400` `BaseErrorResponse`
     */
    dictionaryItemControllerGetDictionaryItemList = (id: number, params: RequestParams = {}) =>
      this.request<GetDictionaryItemsRes, BaseErrorResponse>({
        path: `/api3/dictionary/${id}/item`,
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Dictionary item
     * @name DictionaryItemControllerCreateDictionaryItem
     * @summary Creates dictionary item
     * @request POST:/api3/dictionary/{id}/item
     * @secure
     * @response `200` `GetDictionaryItemRes`
     * @response `400` `BaseErrorResponse`
     */
    dictionaryItemControllerCreateDictionaryItem = (
      id: number,
      data: AddDictionaryItemReq,
      params: RequestParams = {},
    ) =>
      this.request<GetDictionaryItemRes, BaseErrorResponse>({
        path: `/api3/dictionary/${id}/item`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Dictionary item
     * @name DictionaryItemControllerUpdateDictionaryItem
     * @summary Updates dictionary item
     * @request PUT:/api3/dictionary/{id}/item/{itemId}
     * @secure
     * @response `200` `GetDictionaryItemRes`
     * @response `400` `BaseErrorResponse`
     */
    dictionaryItemControllerUpdateDictionaryItem = (id: number, itemId: number, data: { priority?: number | null; value?: string }, params: RequestParams = {}) =>
      this.request<GetDictionaryItemRes, BaseErrorResponse>({
        path: `/api3/dictionary/${id}/item/${itemId}`,
        method: 'PUT',
        body: data,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Dictionary item
     * @name DictionaryItemControllerDeleteDictionaryItem
     * @summary Deletes dictionary item
     * @request DELETE:/api3/dictionary/{id}/item/{itemId}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    dictionaryItemControllerDeleteDictionaryItem = (
      id: number,
      itemId: number,
      query?: {
        /**
         * Hard remove item
         * @example true
         */
        hardRemove?: boolean;
      },
      params: RequestParams = {},
    ) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/dictionary/${id}/item/${itemId}`,
        method: 'DELETE',
        query,
        secure: true,
        format: 'json',
        ...params,
      });
  }

  return new Dictionary();
}
